import { useState, useCallback } from 'react';
import { message } from 'antd';
import { j207Api, FJ170, FJ171 } from '../../../services/j207Api';
import { Mode } from './useJ207Mode';

export function useJ207Record() {
  const [record, setRecord] = useState<FJ170 | null>(null);
  const [formData, setFormData] = useState<Partial<FJ170>>({});
  const [details, setDetails] = useState<Partial<FJ171>[]>([]);
  const [loading, setLoading] = useState(false);

  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await j207Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setDetails(data.details || []);
    } catch {
      message.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const setField = useCallback((key: keyof FJ170, value: FJ170[keyof FJ170]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const getField = useCallback((key: keyof FJ170): FJ170[keyof FJ170] => {
    return (formData as Record<string, FJ170[keyof FJ170]>)[key] ?? '';
  }, [formData]);

  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FJ170) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FJ170,
        details: details as FJ171[],
      };
      if (mode === 'add') {
        const result = await j207Api.create(req);
        message.success(`新增成功: ${result.fj170_01}`);
        onSuccess(result);
      } else {
        const result = await j207Api.update(formData.fj170_01!, req);
        message.success('修改成功');
        onSuccess(result);
      }
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : '儲存失敗';
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  }, [formData, details]);

  const handleDelete = useCallback(async (onSuccess: () => void) => {
    if (!record?.fj170_01) return;
    setLoading(true);
    try {
      await j207Api.delete(record.fj170_01);
      message.success('刪除成功');
      onSuccess();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [record]);

  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record]);

  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData({ fj170_80: 'N' });
    setDetails([]);
  }, []);

  const applyResult = useCallback((result: FJ170) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  const addDetailRow = useCallback(() => {
    setDetails(prev => [
      ...prev,
      {
        fj171_02: prev.length + 1,
        fj171_10: '', fj171_11: '', fj171_12: '', fj171_13: '',
        fj171_14: '', fj171_15: 0,
      },
    ]);
  }, []);

  const deleteDetailRow = useCallback((idx: number) => {
    setDetails(prev => prev.filter((_, i) => i !== idx));
  }, []);

  return {
    record, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow,
  };
}
